import { Button, Link, Input } from '@uswitch/spark'
import { UswArrowLargeRight, UswLocationFill, UswCrossCircleFill } from '@uswitch/spark-icons/usw'
import React, { useState } from 'react'
import uuidv1 from 'uuid/v1'

import Address from '../../../address-matcher/address'
import FormErrorMessage from '../../../address-matcher/form-error-message'
import { usePageContext } from '~/contexts/page-context'
import { useInBucket } from '~/hooks/use-in-bucket'
import { RESULTS_PATH, TV_RESULTS_PATH } from '~/routes-list'
import { isBBHomepage } from '~/utils/page'
import { addressEntryForm } from '~/utils/tracking-constants'

import AddressDropdown from './address-dropdown'
import * as st from './input-form-style'
import ProviderDropdown from './provider-dropdown'

export const InputForm = ({
  ctaText = 'Find deals',
  variant = '',
  addressUnderlay,
  providersData,
  providersUnderlay
}) => {
  const { pageId, scope, slug } = usePageContext()
  const [formId] = useState(uuidv1())

  const getFormUrl = () => {
    if (scope === 'tv') return TV_RESULTS_PATH

    return isBBHomepage(pageId) ? RESULTS_PATH : `${RESULTS_PATH}${slug ? `?source-slug=${slug}` : ''}`
  }

  const formUrl = getFormUrl()

  const isBlackFriday = useInBucket('experiment-black-friday-2023', 'variant')

  return (
    <form
      action={formUrl}
      sx={{ ...st.formStyles(variant), ...(isBlackFriday && st.blackFriday) }}
      data-controller='postcode-form tracking'
      data-postcode-form-form-id-value={formId}
      data-postcode-form-target='form'
      data-action='submit->postcode-form#submit'
      data-tracking-event-value='Form Loaded'
      data-tracking-properties-value={JSON.stringify(
        addressEntryForm({ formId: variant, formName: variant, formStartedId: formId })
      )}
      data-modal-target='form'
      data-nerd
      data-tracking-id={variant}
    >
      <div sx={st.addressMatcherContainer}>
        <Address
          data-postcode-form-target='addressInput'
          data-action='click->postcode-form#editAddress'
          className='element-hidden fs-mask'
        />

        <div sx={st.inputWrapper}>
          <Input
            data-postcode-form-target='postcode'
            data-action='input->postcode-form#changePostcode click->postcode-form#clickPostcode'
            data-cypress-id='postcode-input'
            autoComplete='off'
            leftIcon={<UswLocationFill size='24' />}
            rightIcon={<ClearButtons />}
            type='text'
            data-type='postcode'
            id='postcode-input'
            data-nerd-capture
            name='postcode'
            placeholder='Enter your postcode'
            defaultValue=''
            value=''
          />
        </div>

        <AddressDropdown addressUnderlay={addressUnderlay} />

        <div data-postcode-form-target='missingPostcodeError' className='element-hidden' sx={st.spacingTop}>
          <FormErrorMessage errorMessage='Please enter your postcode e.g. SE1 2LH' />
        </div>
      </div>

      <ProviderDropdown providersData={providersData} providersUnderlay={providersUnderlay} />

      <div data-postcode-form-target='missingProviderError' className='element-hidden'>
        <FormErrorMessage errorMessage='Please select your current provider' />
      </div>

      <Button
        data-cypress-id='input-form-submit'
        variant='primary'
        type='submit'
        fullWidth
        rightIcon={<UswArrowLargeRight strokeWidth={3} sx={st.formButtonReducedArrow} />}
        size='large'
        sx={st.formButtonReducedHomepage}
      >
        <span>{ctaText}</span>
      </Button>

      <Link
        newTab
        text='Privacy notice'
        to='/about-us/privacy-policy/'
        sx={st.formBottomLink}
        data-action='click->tracking#trackSegment'
        data-tracking-event-param='Element Clicked'
        data-tracking-properties-param={JSON.stringify({
          element_name: 'Privacy notice',
          element_text: 'Privacy notice',
          element_type: 'link'
        })}
      />
    </form>
  )
}

const ClearButtons = () => {
  return (
    <>
      <UswCrossCircleFill
        size='24'
        data-action='click->postcode-form#filledInPostcodeState'
        data-postcode-form-target='clearAddressButton'
        className='element-hidden'
        sx={{ zIndex: 3, display: 'none' }}
      />
      <UswCrossCircleFill
        size='24'
        data-action='click->postcode-form#clearPostcodeBox'
        data-postcode-form-target='clearPostcodeBoxButton'
        className='element-hidden'
        sx={{ cursor: 'pointer' }}
      />
    </>
  )
}

export default InputForm
